import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { RemixModalMetadata } from '@atlassian/editor-plugin-malleable-ui/malleableUiPluginType';

import type { AiSuggestionsPlugin } from '../../../aiSuggestionsPluginType';

/**
 * The semantic remix request built for an accepted `SUGGESTED_REMIX` suggestion. These are the
 * "what to generate" bits the backend supplies; the remix engine fills in the runtime/environment
 * context (live content snapshot, space, product, etc.) itself.
 *
 * A `RemixModalMetadata` narrowed to the Suggested Remix source, so it can be handed straight to
 * `malleableUi.commands.startRemix`.
 */
export type SuggestedRemixMetadata = RemixModalMetadata & {
	remixSource: 'suggestedRemix';
};

/**
 * A fully-resolved suggested-remix request: the anchor `localIds` plus the metadata the remix
 * engine consumes. `triggerSuggestedRemix` returns this so the follow-up engine wiring (and tests)
 * can drive generation without re-deriving anything.
 */
export type SuggestedRemixRequest = {
	localIds?: string[];
	metadata: SuggestedRemixMetadata;
};

/**
 * Maps the backend-supplied `creationContext` + prompt onto the metadata the remix engine consumes.
 * Pure and serialisable, so it can be unit-tested without standing up an editor.
 */
export function buildSuggestedRemixMetadata({
	creationContext,
	userPrompt,
}: {
	creationContext?: Record<string, unknown>;
	userPrompt?: string;
}): SuggestedRemixMetadata {
	const context = creationContext ?? {};
	const additionalContext = context.additionalContext as
		| { aspectRatio?: string | null }
		| undefined;

	return {
		userPrompt,
		dynamicUiType: context.dynamicUiType as SuggestedRemixMetadata['dynamicUiType'],
		dynamicUiSubtype: context.dynamicUiSubtype as string | undefined,
		aspectRatio: additionalContext?.aspectRatio,
		remixSource: 'suggestedRemix',
	};
}

/**
 * Resolves an accepted `SUGGESTED_REMIX` suggestion into a remix request and drives generation via
 * `malleableUi.commands.startRemix`. The backend supplies the semantic bits
 * (`creationContext.dynamicUiType` / `dynamicUiSubtype`, `userPrompt`, anchor `localIds`); the remix
 * engine owns generation, the in-editor thinking bar, insertion and cleanup.
 *
 * `malleableUi` is an optional dependency, so when it is absent (e.g. products that do not enrol the
 * remix plugin) this resolves the request without dispatching — keeping the plugin usable everywhere.
 * The resolved request is still returned so callers and tests can assert on it.
 *
 * Returns `undefined` when there is no editor API to drive.
 */
export function triggerSuggestedRemix({
	api,
	creationContext,
	localIds,
	userPrompt,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	creationContext?: Record<string, unknown>;
	localIds?: string[];
	userPrompt?: string;
}): SuggestedRemixRequest | undefined {
	if (!api?.core) {
		return undefined;
	}

	const request: SuggestedRemixRequest = {
		localIds,
		metadata: buildSuggestedRemixMetadata({ creationContext, userPrompt }),
	};

	const startRemix = api.malleableUi?.commands?.startRemix;
	if (startRemix) {
		api.core.actions.execute(
			startRemix({ localIds: request.localIds, remixModalMetadata: request.metadata }),
		);
	}

	return request;
}
